import { useEffect, useState } from 'react'

/**
 * Subscribes to a CSS media query and reports whether it currently matches.
 *
 * Subscribed, never sampled: a resize across the breakpoint re-renders in the
 * same mount. The initial value is read synchronously in the `useState`
 * initializer so the FIRST paint is already correct (no expanded-then-collapse
 * flash on a narrow viewport).
 *
 * Total: returns `false` and never throws where `window.matchMedia` is absent
 * (the vitest node environment has no `window` at all).
 *
 * @param query a CSS media query, e.g. `(max-width: 64rem)`
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return

    const mql = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)

    // The initial value came from the `useState` initializer above; this only
    // subscribes to FUTURE changes (a synchronous setState here would trip
    // react-hooks/set-state-in-effect).
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])

  return matches
}
